import * as t from 'io-ts';

import {RAsyncStatus} from '../../store/appTypes';
import {makeApiType, makeEnumRuntimeType, RLazyTeammateIdRef, RLocalApplicationIdRef} from './apiTypes';
import {RLocalApplicationChannelType} from './localApplicationChannelTypeModel';
import {RLocalApplicationEntryPoint} from './localApplicationEntryPointModel';
import {RLocalApplicationServer} from './localApplicationServerModel';
import {ROauthClient} from './oauthClientModel';
import {parseTeammate, RTeammate} from './teammateModel';

/*
 * Constants.
 */

export enum LocalApplicationFeaturesEnum {
  INBOX_SIDEPANEL = 'inbox-side-panel',
  CHANNEL = 'channel',
}
export const RLocalApplicationFeature = makeEnumRuntimeType(LocalApplicationFeaturesEnum);

export enum LocalApplicationStatusesEnum {
  PRIVATE = 'private',
  PUBLIC = 'public',
}
export const RLocalApplicationStatus = makeEnumRuntimeType(LocalApplicationStatusesEnum);

/*
 * Runtime types.
 */

const RLocalApplicationServers = t.readonly(
  t.type({
    status: RAsyncStatus,
    items: t.readonlyArray(RLocalApplicationServer),
  }),
);

const RLocalApplicationChannelTypeConfig = t.readonly(
  t.intersection([
    t.type({
      id: t.number,
    }),
    t.partial({
      status: RAsyncStatus,
      value: RLocalApplicationChannelType,
    }),
  ]),
);

const RLocalApplicationEntryPoints = t.readonly(
  t.type({
    status: RAsyncStatus,
    items: t.readonlyArray(RLocalApplicationEntryPoint),
  }),
);

const RLocalApplicationOAuthClient = t.readonly(
  t.type({
    status: RAsyncStatus,
    value: t.union([t.undefined, ROauthClient]),
  }),
);

const RLocalApplicationDirty = t.readonly(
  t.intersection([
    t.type({
      id: t.Integer,
      applicationUid: t.string,
      url: t.string,
      installationUrl: t.string,
      name: t.string,
      status: RLocalApplicationStatus,
      createdAt: t.Integer,
      updatedAt: t.Integer,
    }),
    t.partial({
      oauthClientId: t.number,
      creatorId: RLazyTeammateIdRef,
      description: t.string,
      channelType: RLocalApplicationChannelTypeConfig,
      servers: RLocalApplicationServers,
      entryPoints: RLocalApplicationEntryPoints,
      oAuthClient: RLocalApplicationOAuthClient,
    }),
  ]),
);

const RLocalApplicationSigningSecret = t.readonly(
  t.type({
    secret: t.string,
  }),
);

/*
 * Types.
 */

export interface LocalApplicationModel extends t.TypeOf<typeof RLocalApplicationDirty> {}
export interface LocalApplicationServersModel extends t.TypeOf<typeof RLocalApplicationServers> {}
export interface LocalApplicationSigningSecretModel extends t.TypeOf<typeof RLocalApplicationSigningSecret> {}
export interface LocalApplicationChannelTypeConfigModel
  extends t.TypeOf<typeof RLocalApplicationChannelTypeConfig> {}
export const RLocalApplication = t.clean<LocalApplicationModel>(RLocalApplicationDirty as any);
RLocalApplicationIdRef.setTargetType(RLocalApplication);

const RLocalApplicationWithCreator = t.clean(
  t.type({application: RLocalApplication, creator: t.union([t.undefined, RTeammate])}),
);
export interface LocalApplicationWithCreatorModel extends t.TypeOf<typeof RLocalApplicationWithCreator> {}

export interface UnsavedLocalApplicationModel
  extends Omit<
    LocalApplicationModel,
    'id' | 'url' | 'installationUrl' | 'createdAt' | 'oauthClientId' | 'status' | 'channelTypeId'
  > {}

/*
 * IO methods.
 */

export function parseLocalApplication(src: any): LocalApplicationModel {
  return {
    id: src.id,
    applicationUid: src.application_uid,
    url: src.url,
    installationUrl: src.installation_url,
    name: src.name,
    oauthClientId: src.oauth_client_id ?? undefined,
    creatorId: src.creator?.id,
    status: RLocalApplicationStatus.is(src.status) ? src.status : LocalApplicationStatusesEnum.PRIVATE,
    description: src.description ?? undefined,
    createdAt: src.created_at,
    updatedAt: src.updated_at ?? 0,
    channelType: parseLocalApplicationChannelTypeConfig(src),
  };
}

function parseLocalApplicationChannelTypeConfig(
  src: any,
): LocalApplicationChannelTypeConfigModel | undefined {
  if (!src.channel_type_id) {
    return undefined;
  }
  return {
    id: src.channel_type_id,
  };
}

export function serializeLocalApplication(src: UnsavedLocalApplicationModel): any {
  return {
    name: src.name,
  };
}

export function parseLocalApplicationAndCreator(src: any): LocalApplicationWithCreatorModel {
  return {
    application: parseLocalApplication(src),
    creator: src.creator && parseTeammate(src.creator),
  };
}

function parseLocalApplicationSigningSecret(src: any): LocalApplicationSigningSecretModel {
  return {
    secret: src.signing_secret,
  };
}

/*
 * API types.
 */

export const ApiLocalApplication = makeApiType('localApplication', RLocalApplication, parseLocalApplication);
export const ApiLocalApplicationWithCreator = makeApiType(
  'localApplicationWithCreator',
  RLocalApplicationWithCreator,
  parseLocalApplicationAndCreator,
);
export const ApiLocalApplicationSigningSecret = makeApiType(
  'localApplicationSigningSecret',
  RLocalApplicationSigningSecret,
  parseLocalApplicationSigningSecret,
);
